import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Plus, X, Clock, User, FileText, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  scheduled: '#3b82f6',
  completed: '#22c55e',
  cancelled: '#ef4444',
};

const SERVICE_TYPES = [
  'Parent Consultation', 'Child Assessment', 'Pickup/Dropoff', 'Tour',
  'Tax Consultation', 'Meeting', 'Onboarding', 'Follow-up', 'Other'
];

export default function CalendarPage({ tenantId, accentColor, clients, module: defaultModule }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [parentsList, setParentsList] = useState([]);

  const accent = accentColor || '#3b82f6';

  const fetchAppointments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/appointments/${tenantId}`);
      setAppointments(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // Fetch parents/clients for dropdown
  useEffect(() => {
    if (!tenantId) return;
    axios.get(`${API}/daycare/${tenantId}/parents`).then(r => setParentsList(r.data)).catch(() => {});
  }, [tenantId]);

  const calendarEvents = appointments.map(a => ({
    id: a.id,
    title: `${a.client_name || 'Appointment'} - ${a.service_type || ''}`,
    start: `${a.date}T${a.start_time || '09:00'}`,
    end: a.end_time ? `${a.date}T${a.end_time}` : undefined,
    backgroundColor: STATUS_COLORS[a.status] || '#3b82f6',
    borderColor: STATUS_COLORS[a.status] || '#3b82f6',
    extendedProps: a,
  }));

  const openNew = (dateInfo) => {
    setEditingItem(null);
    setFormData({
      date: dateInfo?.dateStr || new Date().toISOString().split('T')[0],
      start_time: '09:00',
      end_time: '10:00',
      status: 'scheduled',
      module: defaultModule || 'general',
      client_id: '',
      client_name: '',
      service_type: '',
      assigned_staff_name: '',
      notes: '',
    });
    setShowForm(true);
  };

  const openEdit = (info) => {
    const a = info.event.extendedProps;
    setEditingItem(a);
    setFormData({ ...a });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/appointments/${tenantId}/${editingItem.id}`, formData);
        toast.success('Appointment updated');
      } else {
        await axios.post(`${API}/appointments/${tenantId}`, formData);
        toast.success('Appointment created');
      }
      setShowForm(false);
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!editingItem || !window.confirm('Delete this appointment?')) return;
    try {
      await axios.delete(`${API}/appointments/${tenantId}/${editingItem.id}`);
      toast.success('Appointment deleted');
      setShowForm(false);
      fetchAppointments();
    } catch { toast.error('Delete failed'); }
  };

  const handleClientSelect = (e) => {
    const pid = e.target.value;
    const parent = parentsList.find(p => p.id === pid);
    setFormData(f => ({
      ...f,
      client_id: pid,
      client_name: parent ? `${parent.first_name} ${parent.last_name}` : '',
    }));
  };

  if (loading) return <div className="cal-loading"><Loader2 className="animate-spin" size={24} /> Loading calendar...</div>;

  return (
    <div className="cal-page" data-testid="calendar-page">
      <div className="cal-header">
        <h2>Appointment Calendar</h2>
        <button className="cal-add-btn" style={{ background: accent, color: '#fff' }} onClick={() => openNew()} data-testid="cal-add-btn">
          <Plus size={16} /> New Appointment
        </button>
      </div>

      <div className="cal-wrap" data-testid="cal-grid">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          events={calendarEvents}
          dateClick={(info) => openNew(info)}
          eventClick={openEdit}
          height="auto"
          editable={false}
          selectable={true}
          dayMaxEvents={3}
        />
      </div>

      {/* Appointment Modal */}
      {showForm && (
        <div className="cal-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="cal-modal" onClick={e => e.stopPropagation()} data-testid="cal-form">
            <div className="cal-modal-header">
              <h3>{editingItem ? 'Edit Appointment' : 'New Appointment'}</h3>
              <button onClick={() => setShowForm(false)} className="cal-close-btn"><X size={18} /></button>
            </div>
            <div className="cal-modal-body">
              <div className="cal-field">
                <label><User size={14} /> Client</label>
                <select value={formData.client_id || ''} onChange={handleClientSelect} data-testid="cal-client-select">
                  <option value="">Select client...</option>
                  {parentsList.map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
              </div>
              <div className="cal-field">
                <label>Service Type</label>
                <select value={formData.service_type || ''} onChange={e => setFormData(f => ({ ...f, service_type: e.target.value }))}>
                  <option value="">Select...</option>
                  {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="cal-row">
                <div className="cal-field">
                  <label>Date</label>
                  <input type="date" value={formData.date || ''} onChange={e => setFormData(f => ({ ...f, date: e.target.value }))} data-testid="cal-date" />
                </div>
                <div className="cal-field">
                  <label>Start</label>
                  <input type="time" value={formData.start_time || ''} onChange={e => setFormData(f => ({ ...f, start_time: e.target.value }))} />
                </div>
                <div className="cal-field">
                  <label>End</label>
                  <input type="time" value={formData.end_time || ''} onChange={e => setFormData(f => ({ ...f, end_time: e.target.value }))} />
                </div>
              </div>
              <div className="cal-field">
                <label>Assigned Staff</label>
                <input type="text" value={formData.assigned_staff_name || ''} onChange={e => setFormData(f => ({ ...f, assigned_staff_name: e.target.value }))} placeholder="Staff name" />
              </div>
              <div className="cal-field">
                <label>Status</label>
                <select value={formData.status || 'scheduled'} onChange={e => setFormData(f => ({ ...f, status: e.target.value }))}>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="cal-field">
                <label><FileText size={14} /> Notes</label>
                <textarea value={formData.notes || ''} onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))} rows={3} placeholder="Notes..." />
              </div>
            </div>
            <div className="cal-modal-footer">
              {editingItem && <button className="cal-delete-btn" onClick={handleDelete} data-testid="cal-delete">Delete</button>}
              <div style={{ flex: 1 }} />
              <button className="cal-cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="cal-save-btn" style={{ background: accent }} onClick={handleSave} data-testid="cal-save">{editingItem ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
